import { useState, type ReactElement } from 'react';
import { useLoadMonotony, type WeekMonotony } from '@/lib/load';

/**
 * Tabla de monotonía / strain / ramp rate de las últimas N semanas.
 *
 * Decisiones:
 * - Los colores de fondo de la celda `monotony` siguen umbrales clásicos
 *   de Foster (Foster, 1998):
 *     - < 1.5  → bajo riesgo  → emerald-100
 *     - 1.5-2  → riesgo medio → amber-100
 *     - > 2.0  → alto riesgo  → rose-100
 *   Mantenemos los mismos colores para `strain` con umbrales proporcionales
 *   (strain >  6000 alto, 4000-6000 medio, resto bajo) — esto es heurístico
 *   y el contrato no lo fija; aceptamos el riesgo de mostrarlo como pista
 *   visual sin promesa de exactitud médica.
 * - Tooltip "qué es" en el header con icono `?` (hover/focus). En lugar de
 *   un dialog modal, usamos `<details>` o `aria-describedby`; aquí
 *   elegimos un `<button>` con `title` y un panel desplegable simple
 *   para no introducir un nuevo componente Dialog (regla del contrato:
 *   reutilizar lo existente, sin nuevos modales).
 * - Si `monotony` o `strain` es `null` (sin variación o no calculable),
 *   mostramos "—" y NO aplicamos color.
 */
export function MonotonyTable({ weeks = 4 }: { weeks?: number }): ReactElement {
  const query = useLoadMonotony(weeks);
  const [openHint, setOpenHint] = useState<string | null>(null);

  return (
    <article
      aria-label="Tabla de monotonía"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        Monotonía últimas {weeks} semanas
      </h2>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="mt-3 text-sm text-slate-600">
          Cargando…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="mt-3 text-sm text-rose-700">
          No se pudo cargar la tabla.
        </p>
      )}
      {query.data && query.data.weeks.length === 0 && (
        <p className="mt-3 text-sm text-slate-600">Sin datos suficientes aún.</p>
      )}

      {query.data && query.data.weeks.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th scope="col" className="py-2 pr-3">Semana</th>
                <ColHeader
                  label="TSS total"
                  hintKey="tss"
                  hint="Suma del TSS diario de la semana. TSS = Training Stress Score."
                  openHint={openHint}
                  onToggle={setOpenHint}
                />
                <ColHeader
                  label="Monotony"
                  hintKey="monotony"
                  hint="Media diaria de TSS dividida por su desviación típica. >2 indica riesgo de sobrecarga (Foster)."
                  openHint={openHint}
                  onToggle={setOpenHint}
                />
                <ColHeader
                  label="Strain"
                  hintKey="strain"
                  hint="TSS total * monotonía. Indicador combinado de carga + falta de variación."
                  openHint={openHint}
                  onToggle={setOpenHint}
                />
                <ColHeader
                  label="Ramp rate"
                  hintKey="ramp"
                  hint="Cambio de CTL (forma 42d) en la semana. >10 por semana suele asociarse a sobrecarga."
                  openHint={openHint}
                  onToggle={setOpenHint}
                />
              </tr>
            </thead>
            <tbody className="text-slate-800">
              {query.data.weeks.map((w) => (
                <Row key={w.weekStartISO} week={w} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

function Row({ week }: { week: WeekMonotony }): ReactElement {
  const monoClass = monotonyClass(week.monotony);
  const strainClass = strainBgClass(week.strain);
  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      <th scope="row" className="py-2 pr-3 font-medium text-slate-900">
        {formatShortWeek(week.weekStartISO)}
      </th>
      <td className="py-2 pr-3">{Math.round(week.tssTotal)}</td>
      <td className={`py-2 pr-3 ${monoClass}`} data-testid="cell-monotony">
        {week.monotony === null ? '—' : week.monotony.toFixed(2)}
      </td>
      <td className={`py-2 pr-3 ${strainClass}`} data-testid="cell-strain">
        {week.strain === null ? '—' : Math.round(week.strain)}
      </td>
      <td className="py-2 pr-3">
        {week.rampRate > 0 ? '+' : ''}
        {week.rampRate.toFixed(1)}
      </td>
    </tr>
  );
}

function ColHeader({
  label,
  hintKey,
  hint,
  openHint,
  onToggle,
}: {
  label: string;
  hintKey: string;
  hint: string;
  openHint: string | null;
  onToggle: (k: string | null) => void;
}): ReactElement {
  const open = openHint === hintKey;
  return (
    <th scope="col" className="py-2 pr-3 align-bottom">
      <span className="inline-flex items-center gap-1">
        <span>{label}</span>
        <button
          type="button"
          aria-label={`Qué es ${label}`}
          aria-expanded={open}
          onClick={() => onToggle(open ? null : hintKey)}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 text-[10px] font-bold text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
          title={hint}
        >
          ?
        </button>
      </span>
      {open && (
        <p
          role="note"
          className="mt-1 max-w-[14rem] whitespace-normal rounded bg-slate-900 px-2 py-1 text-[11px] font-normal normal-case text-slate-50"
        >
          {hint}
        </p>
      )}
    </th>
  );
}

function monotonyClass(m: number | null): string {
  if (m === null) return '';
  if (m < 1.5) return 'bg-emerald-100';
  if (m <= 2.0) return 'bg-amber-100';
  return 'bg-rose-100';
}

function strainBgClass(s: number | null): string {
  if (s === null) return '';
  if (s < 4000) return 'bg-emerald-100';
  if (s <= 6000) return 'bg-amber-100';
  return 'bg-rose-100';
}

function formatShortWeek(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
